import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Disclaimer = () => {
  return (
    <>
      <SEO 
        title="Disclaimer - BlogCraft"
        description="BlogCraft's Disclaimer - Important information about the content and services provided on our website."
        keywords="disclaimer, legal disclaimer, blogcraft disclaimer, content disclaimer"
      />

      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Disclaimer</h1>
          <p className="text-blue-100">Last updated: January 3, 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">General Disclaimer</h2>
                <p className="text-gray-700 mb-4">
                  The information provided by BlogCraft on our website (blogcraft.shop) is for general informational 
                  purposes only. All information on the Site is provided in good faith, however we make no representation 
                  or warranty of any kind, express or implied, regarding the accuracy, adequacy, validity, reliability, 
                  availability, or completeness of any information on the Site.
                </p>
                <p className="text-gray-700">
                  Under no circumstance shall we have any liability to you for any loss or damage of any kind incurred 
                  as a result of the use of the site or reliance on any information provided on the site. Your use of 
                  the site and your reliance on any information on the site is solely at your own risk.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Content Disclaimer</h2>
                <p className="text-gray-700 mb-4">
                  The content on BlogCraft is intended for educational and informational purposes only. While we strive 
                  to provide accurate and up-to-date information, we make no warranties or representations about the 
                  completeness, accuracy, reliability, suitability, or availability of the content.
                </p>
                <p className="text-gray-700 mb-4">
                  The views and opinions expressed in the articles on this site are those of the authors and do not 
                  necessarily reflect the official policy or position of BlogCraft or its owners.
                </p>
                <p className="text-gray-700">
                  Content on this website may become outdated over time. We are not obligated to update or correct 
                  any information that may become inaccurate or incomplete.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Advice Disclaimer</h2>
                <p className="text-gray-700 mb-4">
                  The information provided on BlogCraft is not intended as professional advice. The content on this 
                  website should not be considered a substitute for professional advice from qualified professionals.
                </p>
                <p className="text-gray-700 mb-4">
                  Specifically, but without limitation:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Health-related content is not medical advice and should not replace consultation with healthcare professionals</li>
                  <li>Financial content is not financial advice and should not replace consultation with financial advisors</li>
                  <li>Legal content is not legal advice and should not replace consultation with legal professionals</li>
                  <li>Technical content may not be suitable for all situations and should be evaluated based on your specific needs</li>
                </ul>
                <p className="text-gray-700">
                  Always seek the advice of qualified professionals with any questions you may have regarding a specific 
                  situation or professional advice.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">External Links Disclaimer</h2>
                <p className="text-gray-700 mb-4">
                  Our website may contain links to third-party websites, products, or services. These external links are 
                  provided for your convenience and information only.
                </p>
                <p className="text-gray-700 mb-4">
                  We have no control over the content, nature, and availability of those external sites. The inclusion 
                  of any links does not necessarily imply a recommendation or endorse the views expressed within them.
                </p>
                <p className="text-gray-700">
                  We are not responsible for the content, accuracy, or opinions expressed on external websites. We 
                  expressly disclaim any liability for errors or omissions in the content of external sites.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">User-Generated Content Disclaimer</h2>
                <p className="text-gray-700 mb-4">
                  BlogCraft may contain user-generated content such as comments, reviews, and feedback. The opinions 
                  expressed in user-generated content are those of the authors and do not necessarily reflect the 
                  views of BlogCraft.
                </p>
                <p className="text-gray-700 mb-4">
                  We are not responsible for the accuracy, reliability, or appropriateness of user-generated content. 
                  We reserve the right to remove any user-generated content that we deem inappropriate, offensive, 
                  or in violation of our terms of service.
                </p>
                <p className="text-gray-700">
                  Users are solely responsible for the content they submit to our website.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Investment and Financial Risk Disclaimer</h2>
                <p className="text-gray-700 mb-4">
                  Any content related to investments, financial markets, or business opportunities on BlogCraft is for 
                  informational purposes only and should not be considered investment advice.
                </p>
                <p className="text-gray-700 mb-4">
                  All investments carry risk, and past performance is not indicative of future results. Never invest 
                  more than you can afford to lose, and always conduct your own research and due diligence before 
                  making any investment decisions.
                </p>
                <p className="text-gray-700">
                  We are not financial advisors and do not provide personalized investment advice. Consult with a 
                  qualified financial professional before making any investment decisions.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Technology and Security Disclaimer</h2>
                <p className="text-gray-700 mb-4">
                  While we strive to maintain the security and reliability of our website, we cannot guarantee that 
                  our website will be free from viruses, malware, or other harmful components.
                </p>
                <p className="text-gray-700 mb-4">
                  Technical content, including tutorials and guides, may become outdated as technology evolves. 
                  Always verify current best practices and security measures before implementing any technical solutions.
                </p>
                <p className="text-gray-700">
                  We are not responsible for any damage to your computer systems, data loss, or other technical 
                  issues resulting from your use of our website or implementation of technical content.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
                <p className="text-gray-700 mb-4">
                  To the maximum extent permitted by law, BlogCraft and its owners, employees, agents, and affiliates 
                  shall not be liable for any indirect, incidental, special, consequential, or punitive damages, 
                  including but not limited to loss of profits, data, or other intangible losses.
                </p>
                <p className="text-gray-700">
                  This limitation of liability applies to any claim, cause of action, or theory of liability arising 
                  out of or in connection with your use of our website or the content contained herein.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Indemnification</h2>
                <p className="text-gray-700">
                  You agree to indemnify and hold harmless BlogCraft and its owners, employees, agents, and affiliates 
                  from and against any and all claims, liabilities, damages, losses, and expenses, including reasonable 
                  attorney's fees, arising out of or in any way connected with your access to or use of our website.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Disclaimer</h2>
                <p className="text-gray-700">
                  We reserve the right to amend this disclaimer at any time. Any changes will be posted on this page 
                  with an updated revision date. Your continued use of the website following any changes constitutes 
                  acceptance of those changes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about this disclaimer, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>Website:</strong> <Link to="/contact" className="text-blue-600 hover:underline">Contact Form</Link></p>
                  <p className="text-gray-700"><strong>Response Time:</strong> We will respond to your inquiry within 7 business days.</p>
                </div>
              </section>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg mt-8">
                <p className="text-gray-800 font-semibold">
                  Important Notice:
                </p>
                <p className="text-gray-700 mt-2">
                  By using BlogCraft, you acknowledge that you have read, understood, and agree to this disclaimer. 
                  If you do not agree with any part of this disclaimer, please do not use our website.
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Disclaimer;
